import React from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import i18n from "../../i18n/i18n";

import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";

import logo from "../../images/pilon_logo_yellow.svg";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { ReactComponent as PhoneIcon } from "feather-icons/dist/icons/phone.svg";
import FlagUS from "../../images/lang/Flag_of_the_United_States.svg";
import FlagES from "../../images/lang/32px-Flag_of_Spain.svg.png";

const HeaderOuter = styled.div`
  ${tw`fixed top-0 left-0 right-0 z-50 bg-white shadow-md`}
  padding: 0.5rem 2rem;
  will-change: transform;
  contain: layout style;
`;
const Header = tw.header`
  flex justify-between items-center
  max-w-screen-xl mx-auto
`;

export const NavLinks = tw.div`inline-block`;

/* hocus: stands for "on hover or focus"
 * hocus:bg-primary-700 will apply the bg-primary-700 class on hover or focus
 */
export const NavLink = tw.a`
  text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
`;

export const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-8 py-3 rounded bg-primary-500 text-gray-100
  hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline
  border-b-0
`;

const CallNowLink = styled.a`
  ${tw`inline-flex items-center gap-1 lg:ml-4 my-2 lg:my-0 px-3 py-1 rounded-full font-semibold tracking-wide transition duration-300 no-underline`}
  font-size: 0.7rem;
  border: 1.5px solid #d5a01a;
  background: #d5a01a;
  color: white;
  &:hover {
    background: #c49217;
    border-color: #c49217;
  }
  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
  }
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};

  img {
    ${tw`w-16 mr-3`}
  }
`;

export const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between`;
export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded hocus:text-primary-500 transition duration-300
`;
export const MobileNavLinks = motion(styled.div`
  ${tw`lg:hidden z-10 fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-lg text-gray-900 bg-white`}
  ${NavLinks} {
    ${tw`flex flex-col items-center`}
  }
`);

export const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center
`;

const LangToggleWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-left: 1.5rem;
`;

const LangLabel = styled.span`
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #595959;
`;

const LangSegmented = styled.div`
  display: inline-flex;
  align-items: center;
  border: 2px solid #d5a01a;
  border-radius: 9999px;
  overflow: hidden;
`;

const LangOption = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 3px 10px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  outline: none;
  &:focus-visible {
    outline: 2px solid #6415FF;
    outline-offset: 2px;
  }
  white-space: nowrap;
  transition: background 0.2s, color 0.2s;
  background: ${props => props.active ? "#d5a01a" : "transparent"};
  color: ${props => props.active ? "white" : "#193b85"};

  &:hover {
    background: ${props => props.active ? "#d5a01a" : "#f5e6b8"};
  }

  img {
    width: 18px;
    height: 12px;
    object-fit: cover;
    border-radius: 2px;
  }
`;

function scrollToHash(hash) {
  const id = hash.replace("#", "");
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
  }
}

const HashNavLink = ({ to, children, onClick, ...rest }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pathname, hash] = to.split("#");
  const targetPath = pathname || "/";

  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) onClick();

    if (location.pathname === targetPath) {
      scrollToHash(hash);
    } else {
      navigate(targetPath);
      // Wait for the new page to render, then scroll
      const observer = new MutationObserver(() => {
        const el = document.getElementById(hash);
        if (el) {
          observer.disconnect();
          el.scrollIntoView({ behavior: "smooth" });
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      // Safety timeout to disconnect observer
      setTimeout(() => observer.disconnect(), 3000);
    }
  };

  return <NavLink href={to} onClick={handleClick} {...rest}>{children}</NavLink>;
};

export default ({ roundedHeaderButton = false, logoLink, links, className, collapseBreakpointClass = "lg" }) => {
  const { t } = useTranslation();
  const currentLang = i18n.language;

  const defaultLinks = [
    <NavLinks key={1}>
      <HashNavLink to="/#about">{t("nav.about")}</HashNavLink>
      <HashNavLink to="/#menu">{t("nav.menu")}</HashNavLink>
      <HashNavLink to="/#contact">{t("nav.contact")}</HashNavLink>
      <HashNavLink to="/viewRestaurant#delivery">{t("nav.delivery")}</HashNavLink>
      <LangToggleWrapper>
        <LangLabel>{t("langLabel")}</LangLabel>
        <LangSegmented role="group" aria-label="Language selection">
          <LangOption active={currentLang === "en"} onClick={() => { i18n.changeLanguage("en"); localStorage.setItem("lang", "en"); }} aria-label="Switch to English" aria-pressed={currentLang === "en"}>
            <img src={FlagUS} alt="" aria-hidden="true" />
            EN
          </LangOption>
          <LangOption active={currentLang === "es"} onClick={() => { i18n.changeLanguage("es"); localStorage.setItem("lang", "es"); }} aria-label="Cambiar a Español" aria-pressed={currentLang === "es"}>
            <img src={FlagES} alt="" aria-hidden="true" />
            ES
          </LangOption>
        </LangSegmented>
      </LangToggleWrapper>
      <CallNowLink href="tel:+18643860676" aria-label="Call El Pilón at (864) 386-0676">
        <PhoneIcon aria-hidden="true" />
        (864) 386-0676
      </CallNowLink>
    </NavLinks>
  ];

  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
    <LogoLink href="/" style={{ color: "#193b85" }}>
      <img src={logo} alt="El Pilón Sabor Latino logo" width="64" height="64" />
      El Pilón Sabor Latino
    </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <HeaderOuter>
      <Header className={className || "header-light"}>
        <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
          {logoLink}
          {links}
        </DesktopNavLinks>

        <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
          {logoLink}
          <MobileNavLinks initial={{ x: "150%", display: "none" }} animate={animation} css={collapseBreakpointCss.mobileNavLinks}>
            {links}
          </MobileNavLinks>
          <NavToggle onClick={toggleNavbar} className={showNavLinks ? "open" : "closed"} aria-label={showNavLinks ? "Close navigation menu" : "Open navigation menu"} aria-expanded={showNavLinks}>
            {showNavLinks ? <CloseIcon tw="w-6 h-6" aria-hidden="true" /> : <MenuIcon tw="w-6 h-6" aria-hidden="true" />}
          </NavToggle>
        </MobileNavLinksContainer>
      </Header>
    </HeaderOuter>
  );
};

/* The below code is for generating dynamic break points for navbar.
 * Using this you can specify if you want to switch
 * to the toggleable mobile navbar at "sm", "md" or "lg" or "xl" above using the collapseBreakpointClass prop
 * Its written like this because we are using macros and we can not insert dynamic variables in macros
 */

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  }
};
